import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Eyebrow, Reveal, Rich } from "./Motion";
import { api } from "../lib/api";

export const CONTACT_EMAIL = "mariella@mfconsultinggroup.it";
export const CONTACT_EMAIL_INFO = "info@mfconsultinggroup.it";
export const CONTACT_PHONE = "+39 351 7866743";
export const CONTACT_VAT = "IT06435540650";
export const CONTACT_ADDRESS = "Via F. Magellano, 84079 Villammare (SA), Italia";

const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "" };

const Field = ({ id, label, value, onChange, type = "text", required, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mono text-[11px] uppercase text-[rgba(16,16,20,0.65)]">{label}{required && " *"}</span>
    <input data-testid={`contact-${id}-input`} type={type} required={required} value={value} onChange={(e) => onChange(id, e.target.value)} className="field-light" />
  </label>
);

const ContactForm = () => {
  const { t, lang } = useLang();
  const C = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/contact", { ...form, lang });
      setStatus("success");
      setForm(EMPTY);
    } catch {
      setStatus("error");
    }
  };
  if (status === "success")
    return (
      <motion.div data-testid="contact-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-[rgba(193,154,75,0.5)] bg-white/70 p-8 lg:p-12 rounded-[6px]">
        <span className="w-12 h-12 rounded-full bg-[var(--fux)] text-white grid place-items-center"><Check className="w-6 h-6" /></span>
        <p className="mt-6 text-xl lg:text-2xl font-semibold leading-snug">{C.success}</p>
        <button data-testid="contact-new-message" onClick={() => setStatus("idle")} className="mt-6 mono text-[12px] uppercase text-[var(--fux)] link-sweep">↺ {C.submit}</button>
      </motion.div>
    );
  return (
    <form data-testid="contact-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
      <Field id="name" label={C.fields.name} value={form.name} onChange={set} required />
      <Field id="company" label={C.fields.company} value={form.company} onChange={set} />
      <Field id="email" type="email" label={C.fields.email} value={form.email} onChange={set} required />
      <Field id="phone" type="tel" label={C.fields.phone} value={form.phone} onChange={set} />
      <label className="block sm:col-span-2">
        <span className="mono text-[11px] uppercase text-[rgba(16,16,20,0.65)]">{C.fields.service}</span>
        <select data-testid="contact-service-select" value={form.service} onChange={(e) => set("service", e.target.value)} className="field-light cursor-pointer">
          <option value="">{C.servicePick}</option>
          {t.services.items.map((s) => <option key={s.t} value={s.t}>{s.t}</option>)}
          <option value={C.logisticsOption}>{C.logisticsOption}</option>
        </select>
      </label>
      <label className="block sm:col-span-2">
        <span className="mono text-[11px] uppercase text-[rgba(16,16,20,0.65)]">{C.fields.message} *</span>
        <textarea data-testid="contact-message-input" required minLength={5} rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="field-light resize-none" />
      </label>
      <div className="sm:col-span-2 flex flex-wrap items-center gap-5">
        <button data-testid="contact-submit-button" type="submit" disabled={status === "sending"}
          className="group inline-flex items-center gap-3 rounded-full bg-[var(--fux)] text-white pl-7 pr-2 py-2 font-semibold hover:bg-[var(--ink)] transition-colors duration-300 disabled:opacity-70">
          {status === "sending" ? C.sending : C.submit}
          <span className="w-10 h-10 rounded-full bg-white text-[var(--fux)] grid place-items-center">
            {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />}
          </span>
        </button>
        {status === "error" && <p data-testid="contact-error" className="text-sm text-red-500">{C.error}</p>}
      </div>
    </form>
  );
};

export const Contact = () => {
  const { t } = useLang();
  const C = t.contact;
  const info = [
    [C.emailLabel, CONTACT_EMAIL, `mailto:${CONTACT_EMAIL}`, "contact-email-link"],
    ["Info", CONTACT_EMAIL_INFO, `mailto:${CONTACT_EMAIL_INFO}`, "contact-email2-link"],
    [C.phoneLabel, CONTACT_PHONE, "tel:+393517866743", "contact-phone-link"],
    ["WhatsApp", CONTACT_PHONE, "https://wa.me/393517866743", "contact-whatsapp-link"],
    [C.vatLabel, CONTACT_VAT, null, "contact-vat"],
    [C.addressLabel, CONTACT_ADDRESS, null, "contact-address"],
  ];
  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-40 bg-[var(--bone)] overflow-hidden">
      <div className="relative max-w-[1440px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10">
        <div className="lg:col-span-5">
          <Eyebrow className="!text-[#b08d1f] [&>span]:!bg-[#b08d1f]">{C.eyebrow}</Eyebrow>
          <Reveal><h2 data-testid="contact-title" className="mt-8 display text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.01em]"><Rich text={C.title} /></h2></Reveal>
          <Reveal delay={0.1}><p className="mt-8 text-base lg:text-lg text-[rgba(16,16,20,0.75)] max-w-md">{C.intro}</p></Reveal>
          <Reveal delay={0.2}>
            <dl data-testid="contact-direct" className="mt-12 border-t border-[rgba(16,16,20,0.15)]">
              <dt className="mono text-[11px] uppercase text-[#b08d1f] pt-6 pb-2">{C.direct}</dt>
              {info.map(([k, v, href, tid]) => (
                <div key={k} className="flex justify-between items-baseline gap-4 py-3 border-b border-[rgba(16,16,20,0.15)]">
                  <span className="text-[rgba(16,16,20,0.65)] text-sm shrink-0">{k}</span>
                  <dd className="mono text-sm text-right">
                    {href ? <a data-testid={tid} href={href} className="hover:text-[var(--fux)] transition-colors">{v}</a> : <span data-testid={tid}>{v}</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
          <Reveal delay={0.25}>
            <div data-testid="contact-place" className="mt-10 relative h-52 lg:h-60 overflow-hidden rounded-t-[999px] rounded-b-[6px]">
              <img src="/img/gulf.jpg" alt="Golfo di Policastro dall'alto" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#16265c]/60 via-transparent to-transparent" />
              <span className="absolute bottom-3 left-3 mono text-[10px] uppercase bg-[#16265c]/75 backdrop-blur text-[var(--bone)] px-2.5 py-1 rounded-sm">Golfo di Policastro · Costiera Cilentana</span>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7"><ContactForm /></Reveal>
      </div>
    </section>
  );
};
